import { notifications } from '@mantine/notifications';
import { IconCheck, IconExclamationCircle } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import type { TrialCreditStatusResponse } from '@xver/@types/bill';
import { Endpoints } from '@xver/apis';
import { ME_QUERY_KEY } from '@xver/hooks/useMeQuery';
import { getErrorMessage } from '@xver/utils/error';

export const TRIAL_CREDIT_QUERY_KEY = ['apis.v1.billing.trial-credit'];

export function useTrialCreditQuery() {
    return useQuery<TrialCreditStatusResponse>({
        queryKey: TRIAL_CREDIT_QUERY_KEY,
        queryFn: Endpoints.v1.Billing.getTrialCreditStatus,
        staleTime: 30_000,
    });
}

export function useClaimTrialCreditMutation() {
    const { t } = useTranslation('profile');
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: () => Endpoints.v1.Billing.claimTrialCredit(),
        onSuccess: async (response) => {
            notifications.show({
                title: t('trial.toast.claimedTitle'),
                message: response?.message ?? t('trial.toast.claimedMessage'),
                icon: <IconCheck size={18} color="#22c55e" />,
            });
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: TRIAL_CREDIT_QUERY_KEY }),
                queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY }),
            ]);
        },
        onError: (err: unknown) => {
            notifications.show({
                title: t('trial.toast.errorTitle'),
                message: getErrorMessage(err) ?? t('trial.toast.errorMessage'),
                icon: <IconExclamationCircle size={18} color="#eb5757" />,
            });
        },
    });
}
